import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Phone } from "lucide-react";
import { showToast } from "src/components/ToastProvider";
import SocialIcons from "src/components/ui-elements/SocialIcons";
import PaymentMethodsRow from "src/components/ui-elements/PaymentMethodsRow";
import { getImageUrl } from "src/utils/imageUrl";
import ToastTypes from "src/enum/ToastTypes";
import { subscribeUser } from "src/services/OfferSubscriptionService";
import { useTheme } from "src/contexts/ThemeContext";
import { formatPhoneNumber } from "src/utils/phoneUtils";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [policies, setPolicies] = useState([]);
  const [footerPages, setFooterPages] = useState({});
  const globalData = useSelector((state) => state.globalData?.globalDataName);
  const blogs = useSelector((state) => state.blogs?.items || []);
  const storePages = useSelector((state) => state.storePages?.items);
  const categories = useSelector((state) => state.category?.categoryArray || []);
  const navigate = useNavigate();
  const { themeConfig } = useTheme();

  const instaLink = globalData.instagram || "";
  const threadsLink = globalData.threads || "";
  const twitterLink = globalData.twitter || "";
  const faceBookLink = globalData.facebook || "";
  const linkedLink = globalData.linkedin || "";

  const isExpertHub = themeConfig?.preset === 'experthub';
  const faqConfig = themeConfig?.faq;
  const faqFooterEnabled = !!(faqConfig?.enabled && faqConfig?.displayInFooter);
  const aboutDescription = globalData.description || "";
  const hasCategoriesWithImages = categories && categories.filter(item => !!item?.imageUrl).length > 0;
  const shouldShowAbout = isExpertHub && aboutDescription && hasCategoriesWithImages;

  // Seller's store address and support phone number, as entered in their
  // business profile — only rendered when the seller has both filled them
  // in AND opted in via the "Show store address & phone number in footer"
  // theme setting (off by default; not every seller wants a public address).
  const showFooterAddress = !!themeConfig?.showFooterAddress;
  // The backend stores pincode as an integer, which silently drops a
  // leading zero (e.g. "08852" -> 8852). US ZIP codes are the only ones in
  // this form that can start with 0, so pad back to 5 digits there.
  const isUSCountry = ["us", "usa", "united states", "united states of america"]
    .includes((globalData.country || "").trim().toLowerCase());
  const rawPincode = globalData.pincode ? String(globalData.pincode) : "";
  const displayPincode =
    isUSCountry && /^\d{4}$/.test(rawPincode) ? `0${rawPincode}` : rawPincode;
  const formattedAddress = [
    globalData.address,
    globalData.city,
    globalData.state,
    displayPincode,
    globalData.country,
  ]
    .filter(Boolean)
    .join(", ");
  const supportCountryCode = globalData.supportContactCountryCode || "+91";
  const supportPhone = globalData.supportContact || "";
  const supportPhoneHref = supportPhone
    ? `tel:${(supportCountryCode + supportPhone).replace(/[^\d+]/g, "")}`
    : "";
  const hasContactInfo = showFooterAddress && !!(formattedAddress || supportPhone);

  const paymentMethods = Array.isArray(themeConfig?.paymentMethods)
    ? themeConfig.paymentMethods
    : [];

  // Load policies from globalData when available
  useEffect(() => {
    if (globalData?.policies) {
      setPolicies(globalData?.policies);
    }
  }, [globalData]);

  // Filter and group pages that should be displayed in footer
  useEffect(() => {
    if (storePages && storePages.length > 0) {
      const footerDisplayPages = storePages.filter(page => 
        page.Display_Location && page.Display_Location.includes("footer")
      );
      
      // Group pages by their Group field
      const groupedPages = footerDisplayPages.reduce((acc, page) => {
        const groupKey = page.Group || "Quick Links";
        if (!acc[groupKey]) {
          acc[groupKey] = [];
        }
        acc[groupKey].push(page);
        return acc;
      }, {});
      
      setFooterPages(groupedPages);
    }
  }, [storePages]);

  // Merge the FAQ plugin into the grouped footer pages when enabled by the seller,
  // so it rides the same column mechanism as seller-created CMS pages.
  const getEffectiveFooterPages = () => {
    const pages = { ...footerPages };
    if (faqFooterEnabled) {
      const groupKey = (faqConfig.footerGroup || "Quick Links").trim();
      // Prepend, not append — an explicit seller toggle should never get
      // silently sliced off by a group's page-count cap (e.g. About Us
      // only shows the first 2 pages).
      pages[groupKey] = [
        { id: "__faq__", title: "FAQs", slug: "faq", link: "/faq" },
        ...(pages[groupKey] || []),
      ];
    }
    return pages;
  };

  // Get About Us pages from grouped pages
  const getAboutUsPages = () => {
    const effectivePages = getEffectiveFooterPages();
    const aboutUsKey = Object.keys(effectivePages).find(key =>
      key.trim().toLowerCase() === "about us"
    );
    return aboutUsKey ? effectivePages[aboutUsKey] : [];
  };

  // Filter out About Us pages from other sections
  const getFilteredFooterPages = () => {
    const filtered = getEffectiveFooterPages();
    const aboutUsKey = Object.keys(filtered).find(key =>
      key.trim().toLowerCase() === "about us"
    );
    if (aboutUsKey) {
      delete filtered[aboutUsKey];
    }
    return filtered;
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    const successMessage = await subscribeUser(email);
    if (successMessage) {
      showToast(successMessage, ToastTypes.SUCCESS);
      setEmail(""); // Clear input after success
    }
    else {
      showToast("Subscription failed. Please try again.", ToastTypes.ERROR);
    }
  };

  const handleViewDetails = (page) => {
    const target = page.link || (page.slug ? `/page/${page.slug}` : null);
    if (target) {
      navigate(target);
    }
  };

  return (
    <footer className="mt-0 md:mt-8 font-outfitRegular py-10 shadow-inner border-t border-gray-200">
      <div className="container mx-auto px-4">
        {/* About Section for ExpertHub Theme - Only show when categories with images exist */}
        {shouldShowAbout && (
          <div className="mb-12 pb-8 border-b border-gray-300">
            <h3 className="text-[24px] sm:text-[28px] font-outfit font-medium mb-4 leading-[30px]">
              About
            </h3>
            <p className="font-outfitRegular text-black text-[15px] leading-relaxed max-w-3xl">
              {aboutDescription}
            </p>
          </div>
        )}

        <div className="lg:flex justify-between mb-[25px] sm:mb-[50px]">
          <div className="lg:w-[470px] ml-0 w-full mb-10 lg:mb-0">
            <div>
              <h3 className="text-[24px] sm:text-[30px] font-outfit font-medium mb-3 leading-[30px]">
                Get access to our best offers — straight to your inbox
              </h3>
              <p className="mb-5 font-outfitRegular text-black">
                Sign up for exclusive offers, updates, and news.
              </p>
              <form className="flex flex-col gap-2 relative" onSubmit={handleSubscribe}>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="py-2 sm:py-4 px-2 sm:px-4 bg-gray-100 rounded-full text-gray-800"
                  required
                />
                <button
                  type="submit"
                  className="absolute right-[10px] top-[4px] sm:top-[8px] flex items-center justify-center rounded-full font-outfitRegular transition w-[32px] sm:w-[40px] h-[32px] sm:h-[40px]"
                  style={{ backgroundColor: 'var(--btn-primary-bg)', color: 'var(--btn-primary-text)', padding: 0 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--btn-primary-hover-bg)';
                    e.currentTarget.style.color = 'var(--btn-primary-hover-text)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--btn-primary-bg)';
                    e.currentTarget.style.color = 'var(--btn-primary-text)';
                  }}
                >
                  <svg
                    className="icon icon-arrow icon--medium w-[24px] h-[24px]"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.5 3.74121L13.75 9.99121L7.5 16.2412"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                  </svg>
                </button>
              </form>
            </div>

            {/* Store Address & Support Contact — shown only when the seller has set them */}
            {hasContactInfo && (
              <div className="mt-4 flex flex-col gap-2">
                {formattedAddress && (
                  <div className="flex items-start gap-1.5">
                    <MapPin size={15} className="flex-shrink-0 mt-0.5 text-gray-500" />
                    <span className="text-sm text-gray-500 font-outfitRegular leading-relaxed">
                      {formattedAddress}
                    </span>
                  </div>
                )}
                {supportPhone && (
                  <a
                    href={supportPhoneHref}
                    className="flex items-center gap-1.5 text-sm text-gray-500 font-outfitRegular hover:text-black transition-colors duration-200 w-fit"
                  >
                    <Phone size={15} className="flex-shrink-0" />
                    <span>
                      {formatPhoneNumber(supportCountryCode, supportPhone)}
                    </span>
                  </a>
                )}
              </div>
            )}

            {/* Accepted Payment Methods */}
            {paymentMethods.length > 0 && (
              <div className="mt-5">
                <h6 className="text-sm font-outfitSemiBold uppercase tracking-wide text-gray-500 mb-2">
                  We accept
                </h6>
                <PaymentMethodsRow methods={paymentMethods} />
              </div>
            )}
          </div>

          <div className="flex-1 flex flex-wrap lg:flex-nowrap gap-x-10 gap-y-6 justify-between lg:justify-around pl-[0px] lg:pl-[100px]">
            {/* About Us Section - Include dynamic About Us pages */}
            <div className="md:flex-1 md:basis-0 min-w-0 w-full mb-6 lg:mb-0">
              <div className="mb-5">
                <h6 className="text-[18px] sm:text-[23px] font-500 text-[#2F2F2F]">About Us</h6>
              </div>
              <div>
                <ul className="flex flex-col space-y-3">
                  <li><a href="/contact" className="text-[#4A4A4A] text-[15px] hover:text-black transition-colors duration-200 font-normal">Contact</a></li>
                     {blogs && blogs.length > 0 && (
                     <li><Link to="/blogs" className="text-[#4A4A4A] text-[15px] hover:text-black transition-colors duration-200 font-normal">Blogs</Link></li>
                     )}

                  {/* Add dynamic About Us pages */}
                  {getAboutUsPages().slice(0, 3).map((page) => (
                    <li key={page.id} style={{ cursor: "pointer" }}>
                      <button 
                        onClick={() => handleViewDetails(page)}
                        className="text-[#4A4A4A] text-[15px] hover:text-black transition-colors duration-200 font-normal text-left p-0 bg-transparent border-none cursor-pointer"
                      >
                        {page.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Quick Links Sections - Dynamic Groups Limited to 3 sections max */}
            {Object.keys(getFilteredFooterPages()).length > 0 && Object.entries(getFilteredFooterPages()).slice(0, 3).map(([groupName, pages]) => (
              <div key={groupName} className="md:flex-1 md:basis-0 min-w-0 w-full mb-6 lg:mb-0">
                <div className="mb-5">
                  <h6 className="text-[18px] sm:text-[23px] font-500 text-[#2F2F2F]">
                    {groupName}
                  </h6>
                </div>
                <div>
                  <ul className="flex flex-col space-y-3">
                    {pages.slice(0, 3).map((page) => (
                      <li key={page.id} style={{ cursor: "pointer" }}>
                        <button 
                          onClick={() => handleViewDetails(page)}
                          className="text-[#4A4A4A] text-[15px] hover:text-black transition-colors duration-200 font-normal text-left p-0 bg-transparent border-none cursor-pointer"
                        >
                          {page.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}

            {/* General Links Section - Limited to 3 rows */}
            {policies && policies.length > 0 && (
              <div className="md:flex-1 md:basis-0 min-w-0 lg:flex lg:flex-col w-full mb-6 lg:mb-0">
                <div>
                  <div className="mb-5">
                    <h6 className="text-[18px] sm:text-[23px] font-500 text-[#2F2F2F]">General links</h6>
                  </div>
                  <div>
                    <ul className="flex flex-col space-y-3">
                       {policies.map((policy) => (
                        <li key={policy.policyName} style={{ cursor: "pointer" }}>
                          <Link to={`/policies/${policy.policyName}`} className="text-[#4A4A4A] text-[15px] hover:text-black transition-colors duration-200 font-normal">{policy.policyName}</Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Disclaimer Section - Show only when verified is false */}
        {globalData?.verified === false && (
          <div className="mb-6 sm:mb-8">
            <div className="bg-gray-50 border-l-4 border-black p-4 rounded-r-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-black-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-black-800 font-outfitRegular">
                    <strong className="font-medium">Disclaimer:</strong> Products and services on this store are offered directly by the seller. Blinkz is not responsible for orders, payments, or any issues arising from your purchase.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

      <div className="border-t border-gray-300 mt-4 sm:mt-8 pt-6 flex flex-col-reverse lg:flex-row justify-between items-center gap-4 w-full">
  {/* Left Side - Powered by Blinkz with Logo */}
  <div className="flex items-center justify-center lg:justify-start w-full lg:w-auto gap-2">
    <span className="text-[#4A4A4A] text-[15px] font-outfitRegular">
      Powered by:
    </span>
    <a 
      href="http://blinkz.in/" 
      target="_blank" 
      rel="noopener noreferrer"
    >
      <img 
        src={getImageUrl('D10003/Logo-Demo-2025-08-03 10:47:08.062Z.png')}
        alt="Blinkz Logo"
        className="h-6 w-auto object-contain cursor-pointer"
      />
    </a>

</div>

          {/* Right Side - Social Icons */}
          <div className="flex gap-4 items-center justify-center lg:justify-end w-full lg:w-auto">
            <div className="flex space-x-4">
              <SocialIcons
                instaLink={instaLink}
                twitterLink={twitterLink}
                faceBookLink={faceBookLink}
                linkedLink={linkedLink}
                threadsLink={threadsLink}
              />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
